import { OneSchemaTemplateColumn } from '@oneschema/react';
import { Global } from '@utils/common';
import { type LockedColumnHeader } from 'types';

export const PAGE_PADDING = 20;
export const BREADCRUMB_HEIGHT = 48;
// TLC: We should remove the beta banner when the beta feature is disabled
export const BETA_BANNER_HEIGHT = 80 + 24;
export const TAB_LIST_HEIGHT = 90;

// Used for decimal display in line item prices
export const MIN_DECIMAL_DISPLAY = 2;
export const MAX_DECIMAL_DISPLAY = 4;

export const BOOLEAN: OneSchemaTemplateColumn['data_type'] = 'BOOLEAN';
// Match how Excel renders the native booleans the buyer export writes
export const BOOLEAN_FALSE_LABEL = 'FALSE';
export const BOOLEAN_TRUE_LABEL = 'TRUE';
export const CATEGORY_TEMPLATE_COLUMN_KEY = 'category';
export const COLUMN_WIDTH_LARGE = 240;
export const COLUMN_WIDTH_MEDIUM = 150;
export const COLUMN_WIDTH_SMALL = 100;
export const DEFAULT_CUSTOM_COLUMN_SUPPLIER_VISIBLE = true;
export const DEFAULT_CUSTOM_COLUMN_ITEM = () => ({
  column_type: Global.lots.custom_column_headers.column_types.descriptive,
  is_supplier_visible: DEFAULT_CUSTOM_COLUMN_SUPPLIER_VISIBLE,
});
export const DEFAULT_DESCRIPTIVE_COLUMN_LIMIT = 10;
export const DEFAULT_LOT_NAME = 'Default';
export const LOT_NAME_TEMPLATE_COLUMN_KEY = 'lot_name';
export const INTERNAL_PART_NUMBER_SKU_TEMPLATE_COLUMN_KEY = 'internal_part_number_sku';
// OneSchema template key — not the grid column id IS_REQUIRED_COLUMN_KEY
export const IS_REQUIRED_TEMPLATE_COLUMN_KEY = 'is_required';
// "Arkestro Internal ID" in the UI — references the LineItem database ID (line_items.id)
export const LINE_ITEM_ID_TEMPLATE_COLUMN_KEY = 'line_item_id';
export const LINE_ITEM_NAME_TEMPLATE_COLUMN_KEY = 'line_item_name';
export const NUMBER: OneSchemaTemplateColumn['data_type'] = 'NUMBER';
export const PICKLIST: OneSchemaTemplateColumn['data_type'] = 'PICKLIST';
export const PICKLIST_OPTION_COLOR_GRAY = 'ColorSelectorGray100';
export const PRICE_PER_UNIT_TEMPLATE_COLUMN_KEY = 'price_per_unit';
export const QUANTITY_OF_UNITS_TEMPLATE_COLUMN_KEY = 'quantity_of_units';
export const STRING_TO_NUMBER_REGEX = /[^0-9.-]+/g;
export const STRING_TO_SANITIZE_NUMBER_REGEX = /[^\d.-]/g;
export const SUGGESTED_PRICE_TEMPLATE_COLUMN_KEY = 'suggested_price';
export const SUPPLIER_PART_NUMBER_SKU_TEMPLATE_COLUMN_KEY = 'supplier_part_number_sku';
export const UNIT_OF_MEASURE_TEMPLATE_COLUMN_KEY = 'unit_of_measure';

// Template columns whose MAPPED values are preserved as descriptive custom columns on import.
// Keyed by template_column_key (language-invariant) — never by the translated display label.
export const STATIC_CUSTOM_COLUMN_KEYS = [
  CATEGORY_TEMPLATE_COLUMN_KEY,
  INTERNAL_PART_NUMBER_SKU_TEMPLATE_COLUMN_KEY,
  SUPPLIER_PART_NUMBER_SKU_TEMPLATE_COLUMN_KEY,
];

// Canonical display labels for the two part-number descriptive custom columns that the
// server seeds by default on price-update events (BidRequest#build_default_lot). These MUST match
// the server's hardcoded display_text verbatim (like the default "Description" column) so the
// column keeps one stable name across seed, sample-download, and re-upload. The upload template
// columns carry a different, longer label ("…/SKU"); getTemplateOverrides adds these canonical
// labels as alternative_names so a filled sample sheet auto-maps, and getCustomMatchedHeaders
// re-emits mapped values under these labels rather than the "…/SKU" template column name.
export const CATEGORY_COLUMN_NAME = 'Category';
export const INTERNAL_PART_NUMBER_COLUMN_NAME = 'Internal Part Number';
export const SUPPLIER_PART_NUMBER_COLUMN_NAME = 'Supplier Part Number';
export const PART_NUMBER_TEMPLATE_KEY_TO_COLUMN_NAME: Record<string, string> = {
  [INTERNAL_PART_NUMBER_SKU_TEMPLATE_COLUMN_KEY]: INTERNAL_PART_NUMBER_COLUMN_NAME,
  [SUPPLIER_PART_NUMBER_SKU_TEMPLATE_COLUMN_KEY]: SUPPLIER_PART_NUMBER_COLUMN_NAME,
};

// Line item column keys
export const ADDITIONAL_FEES_COLUMN_KEY = 'additional_fees';
export const ALL_IN_PRICE_COLUMN_KEY = 'all_in_price';
export const BASELINE_PRICE_COLUMN_KEY = 'baseline_price';
export const BASELINE_PRICE_SOURCE_COLUMN_KEY = 'baseline_price_source';
export const DELETE_LINE_ITEM_COLUMN_KEY = 'delete_line_item';
export const DESCRIPTION_COLUMN_KEY = 'description';
export const DISCOUNT_COLUMN_KEY = 'discount';
export const EXTENDED_QUANTITY_COLUMN_KEY = 'extended_quantity';
export const ID_COLUMN_KEY = 'id';
export const IS_REQUIRED_COLUMN_KEY = 'is_required';
export const ITEM_INTEL_COLUMN_KEY = 'item_intel';
export const LOT_LINE_NUMBER_COLUMN_KEY = 'lot_line_number';
export const NAME_COLUMN_KEY = 'name';
export const NEGOTIATION_PRICE_SOURCE_COLUMN_KEY = 'negotiation_price_source';
// Write-once flag: true when the buyer's upload carried a price-per-unit (> 0) for the row. Set at
// import, never changed after, and read by the NSB seeder guard so NSB skips user-provided rows.
export const IS_BUYER_PROVIDED_BASELINE_PRICE_COLUMN_KEY = 'is_buyer_provided_baseline_price';
export const NEGOTIATION_STARTING_PRICE_COLUMN_KEY = 'negotiation_starting_price';
export const NSB_BENCHMARK_COLUMN_KEY = 'nsb_benchmark';
export const PRICE_COLUMN_KEY = 'price';
export const QUANTITY_COLUMN_KEY = 'quantity';
export const RESOLVED_LINE_ITEM_NAME_COLUMN_KEY = 'resolved_line_item_name';
export const SNO_COLUMN_KEY = 'sno';
export const SUGGESTED_PRICE_COLUMN_KEY = 'suggested_price_override';
export const SUPPLIER_QUANTITY_COLUMN_KEY = 'supplier_quantity';
export const SYSTEM_MODIFIED_COLUMN_KEY = 'system_modified';
export const UNITS_COLUMN_KEY = 'units';

// Column IDs that are valid for line items in the Line Items Table
export const VALID_LINE_ITEM_COLUMN_IDS = [
  ADDITIONAL_FEES_COLUMN_KEY,
  ALL_IN_PRICE_COLUMN_KEY,
  BASELINE_PRICE_COLUMN_KEY,
  DELETE_LINE_ITEM_COLUMN_KEY,
  DESCRIPTION_COLUMN_KEY,
  DISCOUNT_COLUMN_KEY,
  EXTENDED_QUANTITY_COLUMN_KEY,
  ID_COLUMN_KEY,
  IS_REQUIRED_COLUMN_KEY,
  LOT_LINE_NUMBER_COLUMN_KEY,
  NAME_COLUMN_KEY,
  NEGOTIATION_STARTING_PRICE_COLUMN_KEY,
  NSB_BENCHMARK_COLUMN_KEY,
  PRICE_COLUMN_KEY,
  SUGGESTED_PRICE_COLUMN_KEY,
  SUPPLIER_QUANTITY_COLUMN_KEY,
  UNITS_COLUMN_KEY,
] as const;

// Column IDs which cannot be modified or deleted in the Line Items Table
export const LOCKED_COLUMNS: LockedColumnHeader[] = [
  ADDITIONAL_FEES_COLUMN_KEY,
  ALL_IN_PRICE_COLUMN_KEY,
  BASELINE_PRICE_COLUMN_KEY,
  DELETE_LINE_ITEM_COLUMN_KEY,
  DISCOUNT_COLUMN_KEY,
  EXTENDED_QUANTITY_COLUMN_KEY,
  ID_COLUMN_KEY,
  IS_REQUIRED_COLUMN_KEY,
  LOT_LINE_NUMBER_COLUMN_KEY,
  NEGOTIATION_STARTING_PRICE_COLUMN_KEY,
  NSB_BENCHMARK_COLUMN_KEY,
  PRICE_COLUMN_KEY,
  SUGGESTED_PRICE_COLUMN_KEY,
  SUPPLIER_QUANTITY_COLUMN_KEY,
  UNITS_COLUMN_KEY,
];
